/**
 * プロジェクトを人に渡す（第 1a 段 = 持ち主を移すだけ・2026-10-21 core/handoff-1a）の、画面から切り離した判断（node --test・projectTransfer.test.ts）。
 * 画面: pages/TransferPage.tsx（渡す = プロジェクトの中の「Hand over」）・pages/TransferIncomingPage.tsx（受け取る = メールのリンクの先）。
 *
 * API の正: apps/server/src/modules/platform/projectTransferRoutes.ts（形は docs/API-REFERENCE.md の「プロジェクトの受け渡し」）。
 * 「何が移るか / 残るか / 作り直すか」の一覧はサーバーが返す（handoff.moves / stays / redo のキー）。画面はキーを文に引くだけ
 *   = 画面が勝手に「移る」と言って実際は移らない、を起こさない（一覧を 2 か所に持たない）。知らないキー（サーバーが先に足した）は出さない。
 *
 * founder の決定（2026-10-21）: ① 受け取った瞬間から利用料は受け取った人が払う（前払いが要る = blockers の add_funds）・
 *   ② 共同作業者は 1b（今回は「渡すと、あなたは開けなくなる」と画面で言い切る）・③ 受け取った後に取り消す待ち時間は無い（確認のダイアログで言う）。
 *
 * import は型だけ（node --experimental-strip-types でそのまま読める。projectRename.ts と同じ決め事）。
 */
import type { MessageKey } from './i18n';

export type TransferStatus = 'invited' | 'accepted' | 'declined' | 'cancelled' | 'expired';

export interface TransferView {
  id: string;
  projectId: string;
  toEmail: string;
  status: TransferStatus;
  createdAt: string;
  expiresAt: string;
  decidedAt: string | null;
  acceptedAt: string | null;
  /** 受け取りの後、渡した人が共同作業者として残るか（2026-10-22 core/handoff-1b）。古いサーバーは送らない = 残らない */
  stayAsMember?: boolean;
}

export interface HandoffItems {
  moves: readonly string[];
  stays: readonly string[];
  redo: readonly string[];
}

export interface HandoffDetails {
  functionSecretNames: string[];
  signInProviders: string[];
  customDomains: string[];
  storeKeys: string[];
}

/** GET /v1/projects/:projectId/transfer（渡す画面） */
export interface OutgoingView {
  transfer: TransferView | null;
  handoff: HandoffItems;
  details: HandoffDetails;
  securityHoldUntil: string | null;
  expiresInDays: number;
}

/** GET /v1/project-transfers/:id（受け取る画面） */
export interface IncomingView {
  transfer: TransferView & { project: { id: string; name: string; slug: string } | null };
  from: { email: string };
  handoff: HandoffItems;
  details: HandoffDetails | null;
  canAccept: boolean;
  blockers: string[];
  ownerSecurityHoldUntil: string | null;
}

/** POST /v1/project-transfers/:id/accept の応答 */
export interface AcceptResult {
  transfer: TransferView;
  project: { id: string; name: string; slug: string };
  newSecretKeys: Array<{ id: string; environment: 'development' | 'production'; token: string; displayPrefix: string }>;
  revokedSecretKeys: number;
  functionsKeys: { reissued: string[]; redeployRequired: string[] };
}

export const outgoingPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/transfer`;
export const incomingApiPath = (id: string) => `/v1/project-transfers/${encodeURIComponent(id)}`;
export const INCOMING_LIST_PATH = '/v1/developers/me/project-transfers';
/** ダッシュボードの受け取りの画面（サーバーのメールのボタンと同じ形 = projectTransferRoutes.ts の incomingPath） */
export const TRANSFER_INCOMING_PATH = '/transfers/:id';
export const incomingPagePath = (id: string) => `/transfers/${encodeURIComponent(id)}`;

/** サーバーの正規化と同じ（前後の空白を落として小文字）。自分のメールへの招きを画面で先に止めるため */
export const normalizeEmail = (raw: string) => raw.trim().toLowerCase();

/**
 * 招きの入力の状態: 'empty' | 'invalid'（@ と . が無い = サーバーの zod の email と同じくらいの粗さ）| 'self'（自分のメール）| 'ok'。
 * WHY 画面で self を止めるか: サーバーは 400 transfer_to_self を返すが、再認証のダイアログを出した後で断ると本人の手間が無駄になる
 */
export type InviteEmailState = 'empty' | 'invalid' | 'self' | 'ok';
export function inviteEmailState(input: string, ownEmail: string | null | undefined): InviteEmailState {
  const e = normalizeEmail(input);
  if (!e) return 'empty';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'invalid';
  if (ownEmail && e === normalizeEmail(ownEmail)) return 'self';
  return 'ok';
}

const ITEM_KEYS: Record<keyof HandoffItems, Record<string, MessageKey>> = {
  moves: {
    games_and_urls: 'transfer.item.games_and_urls',
    players: 'transfer.item.players',
    functions_and_data: 'transfer.item.functions_and_data',
    public_keys: 'transfer.item.public_keys',
    custom_domains: 'transfer.item.custom_domains',
    sign_in_settings: 'transfer.item.sign_in_settings',
    // 売上の行（future_earnings）は呼ぶ側が出金の開閉で選ぶ（下の itemKeys の extra・components/HandoffLists.tsx）。閉じている間は利用料だけの文
    future_earnings: 'transfer.item.future_usage'
  },
  stays: {
    prepaid_balance: 'transfer.item.prepaid_balance',
    // earnings_before・payout_account（売上・振込先）は出金が開いているときだけ呼ぶ側が extra で足す（無ければ出さない）
    unpaid_usage_before: 'transfer.item.unpaid_usage_before'
  },
  redo: {
    secret_keys: 'transfer.item.secret_keys',
    ai_connections: 'transfer.item.ai_connections',
    store_keys: 'transfer.item.store_keys',
    ads_txt: 'transfer.item.ads_txt',
    scheduled_mail: 'transfer.item.scheduled_mail'
  }
};

/**
 * 一覧のキー → 文のキー。知らないキーは落とす（サーバーが先に足した項目で、画面に生のキーを出さない）。
 * extra = 呼ぶ側が足す / 置き換える文（売上・振込先の行）。WHY 呼ぶ側か: 初回の公開では出金（売上と振込先）を閉じている（launchHidden.ts の payouts・
 *   wordsPass.test.ts = 閉じた機能の言葉は切り替えの中でだけ使う）。このファイルは型の import しか持たない（node のテスト）= 切り替えを読めない
 */
export function itemKeys(group: keyof HandoffItems, keys: readonly string[], extra: Partial<Record<string, MessageKey>> = {}): MessageKey[] {
  return keys.map((k) => extra[k] ?? ITEM_KEYS[group][k]).filter((k): k is MessageKey => !!k);
}

const BLOCKER_KEYS: Record<string, MessageKey> = {
  add_funds: 'transfer.blocker.add_funds',
  confirm_email: 'transfer.blocker.confirm_email',
  account_closed: 'transfer.blocker.account_closed',
  project_unavailable: 'transfer.blocker.project_unavailable',
  owner_security_hold: 'transfer.blocker.owner_security_hold'
};

/** 受け取れない理由の文（サーバーの順 = 直す順のまま）。知らない理由は「今は受け取れません」1 つにまとめる */
export function blockerKeys(blockers: readonly string[]): MessageKey[] {
  const out: MessageKey[] = [];
  for (const b of blockers) {
    const k = BLOCKER_KEYS[b] ?? 'transfer.blocker.other';
    if (!out.includes(k)) out.push(k);
  }
  return out;
}

/** 状態の文（受け取りの画面で、開いていない招きを開いたとき） */
export const statusKey = (s: TransferStatus): MessageKey => `transfer.status.${s}` as MessageKey;

/** 受け取りのボタンを押せるか（送信中は押せない = 二重に送らない。サーバーも二重には受け取らない） */
export const canPressAccept = (v: Pick<IncomingView, 'canAccept'> | null, busy: boolean) => !!v?.canAccept && !busy;
